import {
    Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, SimpleChanges
} from '@angular/core';
import { DropdownItem } from '../../model/dropdown-item.model';
import { TranslateService } from '@ngx-translate/core';
import { TestOption } from './test-option.model';

@Component({
    selector: 'ssf-dropdown',
    template: require('./dropdown.component.html'),
    styles: [require('./dropdown.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/**
 * Pure presentation dropdown component. Due to possible cross-validations, it has rather complex input changes management.
 *
 * Limits: The type of the options has to implement DropdownItem interface.
 *
 * Warning: This component might cause problems, if it got placeholder or deselected texts, that match some of content option ids,
 * which is highly unlikely.
 */
export class DropdownComponent implements OnChanges {
    @Input() options: DropdownItem[];
    // id of one of the options, that should be pre-selected
    @Input() preselectedId: string;
    // determines how to treat item's description
    @Input() useTranslation: boolean;
    // if true, provides deselect option, when any option is picked
    @Input() allowDeselect: boolean;
    // if true, whole dropdown is disabled and not clickable
    @Input() disabled: boolean;
    // translation string identifying path to translation file
    @Input() disabledTitleI18n: string;
    @Output() onPick = new EventEmitter<DropdownItem>();
    // 'any' will serve as wild card for sending 'null', because we just need to raise an event, not send anything with it
    @Output() onDeselect = new EventEmitter<any>();
    // stores actually picked option
    selected: DropdownItem;
    // text used, when nothing is selected
    placeholder: DropdownItem;
    shown = false;
    // an indication, that user has already picked anything or deselected an item
    tamperedWith = false;
    i18n = 'SHARED.DROPDOWN.';



    constructor(translate: TranslateService) {
        let placeholderText = translate.instant(this.i18n + 'PLACEHOLDER');
        this.placeholder = new TestOption(placeholderText, placeholderText);
        this.selected = this.placeholder;
    }

    /**
     * Due to possible cross validations, pre-selected option and page refreshes, we need to react on changes, which
     * can't be done with ngOnInit only, but this process is still supposed to be thought of as initialization.
     * The ngOnChanges method checks for changes in either preselectedId or options and sets the selected item
     * according to preselectedId.
     */
    ngOnChanges(changes: SimpleChanges): void {

        // reset selected
        if (this.options) {
            let option = this.findOptionById(this.preselectedId);
            let id = option ? option.getId() : undefined;

            if (id !== this.preselectedId) {
                this.selected = this.placeholder;
            }
        }

        if ((!!changes.preselectedId || !!changes.options) && !this.tamperedWith) {
            if (!!this.preselectedId && !!this.options) {
                this.selected = this.findOptionById(this.preselectedId);
            }
        }
    }

    pickOption(option: DropdownItem): void {
        this.tamperedWith = true;
        if (option.isDisabled()) {
            return;
        }
        this.selected = option;
        this.onPick.emit(this.selected);
        this.toggle();
    }

    /**
     * Called when user clicks the cross icon. The method is supposed to invoke state, where nothing is selected.
     */
    public deselect(event: MouseEvent): void {
        this.tamperedWith = true;
        // forbids parrent component to also fire its onClick behaviour
        event.stopPropagation();
        this.selected = this.placeholder;
        this.onDeselect.emit(null);
        this.hide();
    }

    /**
     * If dropdown menu is shown, it hides it. If it is hidden, it is shown.
     */
    public toggle(): void {
        this.shown = !this.shown;
    }

    /**
     * Hides dropdown menu.
     */
    public hide(): void {
        this.shown = false;
    }

    private findOptionById(id: string): DropdownItem {
        return this.options.find((item: DropdownItem) => {
            return item.getId() === id;
        });
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/modules/shared/components/dropdown/dropdown.component.ts